if (!customElements.get('bundle-included-products')) {
  customElements.define(
    'bundle-included-products',
    class BundleIncludedProducts extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.bundle-included-products__viewport');
        this.track = this.querySelector('.bundle-included-products__track');
        this.indicator = this.querySelector('.bundle-included-products__scroll-indicator');
        this.scrollTrack = this.querySelector('.bundle-included-products__scroll-track');
        this.thumb = this.querySelector('.bundle-included-products__scroll-thumb');

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onIndicatorPointerDown = this.onIndicatorPointerDown.bind(this);
        this.onIndicatorPointerMove = this.onIndicatorPointerMove.bind(this);
        this.onIndicatorPointerUp = this.onIndicatorPointerUp.bind(this);

        if (this.dataset.source === 'storefront_api') {
          this.loadFromStorefront();
        }

        this.bindScrollInteractions();

        if (typeof ResizeObserver !== 'undefined' && this.viewport) {
          this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
          this.resizeObserver.observe(this.viewport);
          if (this.track) this.resizeObserver.observe(this.track);
        }

        // Images / fonts can change scrollWidth after first paint
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', () => this.updateIndicator(), { once: true });
          }
        });

        requestAnimationFrame(() => this.updateIndicator());
        setTimeout(() => this.updateIndicator(), 250);
      }

      disconnectedCallback() {
        this.unbindScrollInteractions();
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
      }

      bindScrollInteractions() {
        if (!this.viewport || this._scrollBound) return;
        this._scrollBound = true;
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
        this.viewport.addEventListener('click', this.onClickCapture, true);

        const scrubTarget = this.indicator || this.scrollTrack;
        if (scrubTarget) {
          scrubTarget.addEventListener('pointerdown', this.onIndicatorPointerDown);
        }
        window.addEventListener('pointermove', this.onIndicatorPointerMove);
        window.addEventListener('pointerup', this.onIndicatorPointerUp);
        window.addEventListener('pointercancel', this.onIndicatorPointerUp);
      }

      unbindScrollInteractions() {
        if (!this._scrollBound) return;
        this._scrollBound = false;
        this.viewport?.removeEventListener('scroll', this.onScroll);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport?.removeEventListener('pointermove', this.onPointerMove);
        this.viewport?.removeEventListener('pointerup', this.onPointerUp);
        this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport?.removeEventListener('wheel', this.onWheel);
        this.viewport?.removeEventListener('click', this.onClickCapture, true);
        const scrubTarget = this.indicator || this.scrollTrack;
        scrubTarget?.removeEventListener('pointerdown', this.onIndicatorPointerDown);
        window.removeEventListener('pointermove', this.onIndicatorPointerMove);
        window.removeEventListener('pointerup', this.onIndicatorPointerUp);
        window.removeEventListener('pointercancel', this.onIndicatorPointerUp);
      }

      getScrollMetrics() {
        if (!this.viewport) {
          return { maxScroll: 0, clientWidth: 0, scrollWidth: 0 };
        }
        const clientWidth = this.viewport.clientWidth;
        const scrollWidth = this.viewport.scrollWidth;
        return {
          clientWidth,
          scrollWidth,
          maxScroll: Math.max(scrollWidth - clientWidth, 0),
        };
      }

      scrubToClientX(clientX) {
        if (!this.viewport || !this.scrollTrack) return;
        const { maxScroll } = this.getScrollMetrics();
        if (maxScroll <= 0) return;

        const rect = this.scrollTrack.getBoundingClientRect();
        const thumbRatio =
          parseFloat(this.scrollTrack.style.getPropertyValue('--bundle-scroll-thumb-width')) || 40;
        const thumbWidthPx = (thumbRatio / 100) * rect.width;
        const usable = Math.max(rect.width - thumbWidthPx, 1);
        const x = Math.min(Math.max(clientX - rect.left - thumbWidthPx / 2, 0), usable);
        const progress = x / usable;
        this.viewport.scrollLeft = progress * maxScroll;
        this.updateIndicator();
      }

      onIndicatorPointerDown(event) {
        if (!this.viewport || !this.scrollTrack) return;
        if (event.button != null && event.button !== 0) return;
        if (event.target.closest('a, button')) return;

        const { maxScroll } = this.getScrollMetrics();
        if (maxScroll <= 2) return;

        event.preventDefault();
        this._scrubPointerId = event.pointerId;
        this.indicator?.classList.add('is-scrubbing');
        this.scrubToClientX(event.clientX);

        try {
          (this.indicator || this.scrollTrack).setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      onIndicatorPointerMove(event) {
        if (this._scrubPointerId !== event.pointerId) return;
        event.preventDefault();
        this.scrubToClientX(event.clientX);
      }

      onIndicatorPointerUp(event) {
        if (this._scrubPointerId !== event.pointerId) return;
        this._scrubPointerId = null;
        this.indicator?.classList.remove('is-scrubbing');
      }

      onPointerDown(event) {
        if (!this.viewport) return;
        // Native touch panning works; drag is for mouse / pen
        if (event.pointerType === 'touch') return;
        if (event.button != null && event.button !== 0) return;
        if (event.target.closest('a, button, input, textarea, select, label, [role="button"]')) {
          return;
        }

        this._dragPointerId = event.pointerId;
        this._dragStartX = event.clientX;
        this._dragStartScroll = this.viewport.scrollLeft;
        this._dragMoved = false;
        this.viewport.classList.add('is-dragging');
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      onPointerMove(event) {
        if (this._dragPointerId !== event.pointerId || !this.viewport) return;
        const dx = event.clientX - this._dragStartX;
        if (Math.abs(dx) > 4) this._dragMoved = true;
        this.viewport.scrollLeft = this._dragStartScroll - dx;
      }

      onPointerUp(event) {
        if (this._dragPointerId !== event.pointerId) return;
        this._dragPointerId = null;
        this.viewport?.classList.remove('is-dragging');
        if (this._dragMoved) {
          this._suppressClick = true;
          window.setTimeout(() => {
            this._suppressClick = false;
          }, 50);
        }
        this._dragMoved = false;
      }

      onClickCapture(event) {
        if (!this._suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }

      onWheel(event) {
        if (!this.viewport) return;
        const { maxScroll } = this.getScrollMetrics();
        if (maxScroll <= 2) return;

        const dominant =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (!dominant) return;

        const atStart = this.viewport.scrollLeft <= 0 && dominant < 0;
        const atEnd = this.viewport.scrollLeft >= maxScroll - 1 && dominant > 0;
        if (atStart || atEnd) return;

        event.preventDefault();
        this.viewport.scrollLeft += dominant;
      }

      onScroll() {
        this.updateIndicator();
      }

      onResize() {
        this.updateIndicator();
      }

      updateIndicator() {
        if (!this.indicator || !this.scrollTrack || !this.viewport) return;

        const { maxScroll, clientWidth, scrollWidth } = this.getScrollMetrics();
        const overflow = maxScroll > 2;

        this.indicator.hidden = !overflow;
        this.indicator.setAttribute('aria-hidden', overflow ? 'false' : 'true');
        this.scrollTrack.setAttribute('aria-orientation', 'horizontal');
        this.scrollTrack.setAttribute('aria-valuemin', '0');
        this.scrollTrack.setAttribute('aria-valuemax', String(Math.round(maxScroll)));
        this.scrollTrack.setAttribute(
          'aria-valuenow',
          String(Math.round(this.viewport.scrollLeft))
        );
        if (!overflow) return;

        const ratio = clientWidth / Math.max(scrollWidth, 1);
        const thumbWidth = Math.min(Math.max(ratio * 100, 18), 100);
        const maxTravel = Math.max(100 - thumbWidth, 0);
        const progress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 0;

        this.scrollTrack.style.setProperty('--bundle-scroll-thumb-width', `${thumbWidth}%`);
        this.scrollTrack.style.setProperty(
          '--bundle-scroll-thumb-left',
          `${progress * maxTravel}%`
        );
      }

      async loadFromStorefront() {
        const token = this.dataset.storefrontToken;
        const shop = this.dataset.shop;
        const handle = this.dataset.productHandle;
        const variantId = this.dataset.variantId;
        const track = this.querySelector('[data-bundle-track]');
        const setupMsg = this.querySelector('[data-bundle-setup-msg]');

        if (!token || !shop || !handle || !track) {
          if (track) {
            track.innerHTML = '';
          }
          return;
        }

        const query = `
          query BundleComponents($handle: String!) {
            product(handle: $handle) {
              variants(first: 25) {
                nodes {
                  id
                  components(first: 25) {
                    nodes {
                      quantity
                      productVariant {
                        title
                        image { url altText }
                        selectedOptions { name value }
                        totalWeight: metafield(namespace: "custom", key: "total_weight") { value }
                        product {
                          id
                          handle
                          title
                          onlineStoreUrl
                          featuredImage { url altText }
                          images(first: 8) {
                            nodes { url altText }
                          }
                          portionSize: metafield(namespace: "custom", key: "portion_size") { value }
                          grade: metafield(namespace: "custom", key: "grade_program") { value }
                          specialty: metafield(namespace: "custom", key: "specialty") { value }
                          aging: metafield(namespace: "custom", key: "aging_technique") { value }
                          frozen: metafield(namespace: "custom", key: "frozen") { value }
                          cut: metafield(namespace: "custom", key: "cut") { value }
                          cutSpec: metafield(namespace: "custom", key: "cut_spec") { value }
                          spec: metafield(namespace: "custom", key: "spec") { value }
                          ingredients: metafield(namespace: "custom", key: "ingredients") { value }
                          allergensCustom: metafield(namespace: "custom", key: "product_allergens") { value }
                          allergensShopify: metafield(namespace: "shopify", key: "allergen-information") {
                            value
                            reference {
                              ... on Metaobject {
                                fields { key value }
                              }
                            }
                            references(first: 10) {
                              nodes {
                                ... on Metaobject {
                                  fields { key value }
                                }
                              }
                            }
                          }
                          country: metafield(namespace: "shopify", key: "country-of-origin") {
                            value
                            reference {
                              ... on Metaobject {
                                fields { key value }
                              }
                            }
                            references(first: 5) {
                              nodes {
                                ... on Metaobject {
                                  fields { key value }
                                }
                              }
                            }
                          }
                          brand: metafield(namespace: "product", key: "brand") {
                            reference {
                              ... on Metaobject {
                                fields { key value }
                              }
                            }
                          }
                          nutrition: metafield(namespace: "custom", key: "nutrition_label") {
                            reference {
                              ... on MediaImage { image { url altText } }
                            }
                          }
                          description: metafield(namespace: "descriptors", key: "subtitle") { value }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        try {
          const response = await fetch(`https://${shop}/api/2025-01/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query, variables: { handle } }),
          });

          if (!response.ok) throw new Error(`Storefront API ${response.status}`);

          const payload = await response.json();
          if (payload.errors?.length) throw new Error(payload.errors[0].message);

          const variants = payload.data?.product?.variants?.nodes || [];
          const matched =
            variants.find((v) => String(v.id).endsWith(`/${variantId}`)) || variants[0];
          const nodes = matched?.components?.nodes || [];

          if (!nodes.length) {
            track.innerHTML =
              '<li class="text-subtext text-sm">No bundled products returned for this variant.</li>';
            return;
          }

          if (setupMsg) setupMsg.remove();

          const sectionId = this.dataset.sectionId || 'bundle';
          track.innerHTML = nodes
            .map((node, index) => this.renderCard(node, sectionId, index))
            .join('');

          this.bindQuickViews();
          this.querySelectorAll('img').forEach((img) => {
            if (!img.complete) {
              img.addEventListener('load', () => this.updateIndicator(), { once: true });
            }
          });
          requestAnimationFrame(() => this.updateIndicator());
          setTimeout(() => this.updateIndicator(), 250);
        } catch (error) {
          console.error('[bundle-included-products]', error);
          track.innerHTML = `<li class="text-subtext text-sm">Could not load bundled products. ${this.escape(
            error.message
          )}</li>`;
        }
      }

      parseMaybeJson(value) {
        if (value == null || value === '') return '';
        if (typeof value !== 'string') return String(value);
        const trimmed = value.trim();
        if (!(trimmed.startsWith('[') || trimmed.startsWith('{'))) return value;
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object') {
                  return item.label || item.name || item.value || JSON.stringify(item);
                }
                return String(item);
              })
              .filter(Boolean)
              .join(', ');
          }
          if (parsed && typeof parsed === 'object') {
            return parsed.label || parsed.name || parsed.value || value;
          }
        } catch (e) {
          /* keep raw */
        }
        return value;
      }

      formatVariantMeta(variant, product) {
        // Mirror pcard-variant-tiles / enriched PLP: "2 pieces | 8 oz"
        const options = variant?.selectedOptions || [];
        const optionValues = options
          .map((option) => (option?.value || '').trim())
          .filter((value) => value && value !== 'Default Title');

        if (optionValues.length >= 2) {
          return `${optionValues[0]} | ${optionValues[1]}`;
        }
        if (optionValues.length === 1) {
          return optionValues[0];
        }

        const title = (variant?.title || '').trim();
        if (title && title !== 'Default Title') {
          return title.includes(' / ') ? title.replace(/\s+\/\s+/g, ' | ') : title;
        }

        const portion = (product?.portionSize?.value || '').trim();
        const spec = (product?.spec?.value || '').trim();
        if (portion && spec) return `${portion} | ${spec}`;
        if (portion) return portion;
        if (spec) return spec;

        return (variant?.totalWeight?.value || '').trim();
      }

      brandName(product) {
        const fields = product.brand?.reference?.fields || [];
        const nameField = fields.find((field) => field.key === 'name');
        return nameField?.value || '';
      }

      metaobjectLabel(metaobject) {
        const fields = metaobject?.fields || [];
        for (const key of ['label', 'name', 'title', 'value']) {
          const field = fields.find((item) => item.key === key);
          if (field?.value) return String(field.value).trim();
        }
        const first = fields.find((item) => item?.value);
        return first?.value ? String(first.value).trim() : '';
      }

      resolveMetafieldLabel(metafield) {
        if (!metafield) return '';
        const single = this.metaobjectLabel(metafield.reference);
        if (single) return single;
        const list = (metafield.references?.nodes || [])
          .map((node) => this.metaobjectLabel(node))
          .filter(Boolean);
        if (list.length) return list.join(', ');
        const raw = metafield.value;
        if (raw == null || raw === '') return '';
        if (String(raw).startsWith('gid://')) return '';
        return this.parseMaybeJson(raw);
      }

      renderCard(node, sectionId, index) {
        const qty = node.quantity || 1;
        const variant = node.productVariant || {};
        const product = variant.product || {};
        const images = (product.images?.nodes || []).filter((img) => img?.url);
        if (!images.length && (variant.image || product.featuredImage)) {
          images.push(variant.image || product.featuredImage);
        }
        const image = variant.image || images[0] || product.featuredImage;
        const weight = this.formatVariantMeta(variant, product);
        const modalId = `BundleQuickView-SF-${sectionId}-${index}`;
        const title = product.title || variant.title || 'Product';
        const url = product.onlineStoreUrl || (product.handle ? `/products/${product.handle}` : '#');
        const description = product.description?.value || '';
        const nutritionUrl = product.nutrition?.reference?.image?.url;
        const nutritionId = `${modalId}-nutrition`;

        const cardImageHtml = image?.url
          ? `<img class="bundle-included-card__image" src="${this.escape(
              image.url
            )}" alt="${this.escape(image.altText || title)}" width="120" height="120" loading="lazy">`
          : `<div class="bundle-included-card__image placeholder-svg"></div>`;

        const gallerySlides = (images.length ? images : image?.url ? [image] : [])
          .map(
            (img, imgIndex) => `
              <div class="bundle-quick-view__slide${imgIndex === 0 ? ' is-active' : ''}" data-index="${imgIndex}">
                <img class="bundle-quick-view__image" src="${this.escape(img.url)}" alt="${this.escape(
                  img.altText || title
                )}" loading="lazy" width="800" height="800">
              </div>`
          )
          .join('');

        const galleryThumbs =
          images.length > 1
            ? `<div class="bundle-quick-view__thumbs" role="list">
                ${images
                  .map(
                    (img, imgIndex) => `
                  <button type="button" class="bundle-quick-view__thumb${
                    imgIndex === 0 ? ' is-active' : ''
                  }" data-index="${imgIndex}" aria-label="Show image ${imgIndex + 1}">
                    <img src="${this.escape(img.url)}" alt="" loading="lazy" width="80" height="80">
                  </button>`
                  )
                  .join('')}
              </div>`
            : '';

        const specs = [
          ['Grade / Program', this.parseMaybeJson(product.grade?.value)],
          ['Product Attribute', this.parseMaybeJson(product.specialty?.value)],
          ['Brand', this.brandName(product)],
          ['Country of Origin', this.resolveMetafieldLabel(product.country)],
          ['Aging Technique', this.parseMaybeJson(product.aging?.value)],
          ['Frozen', this.parseMaybeJson(product.frozen?.value)],
          ['Packaging', this.parseMaybeJson(product.portionSize?.value)],
          ['Cut Type', this.parseMaybeJson(product.cut?.value)],
          [
            'Cut Spec',
            this.parseMaybeJson(product.cutSpec?.value) ||
              this.parseMaybeJson(product.spec?.value),
          ],
          ['Ingredients', this.parseMaybeJson(product.ingredients?.value)],
          [
            'Allergens',
            this.resolveMetafieldLabel(product.allergensShopify) ||
              this.parseMaybeJson(product.allergensCustom?.value),
          ],
        ].filter((row) => row[1]);

        const specsRows = specs
          .map(
            ([label, value]) =>
              `<tr><th scope="row">${this.escape(label)}</th><td>${this.escape(
                value
              )}</td></tr>`
          )
          .join('');

        const nutritionHtml = nutritionUrl
          ? `<a href="#${nutritionId}" class="bundle-specs-table__nutrition" data-bundle-nutrition-open="${nutritionId}">
              <span class="bundle-specs-table__nutrition-icon" aria-hidden="true">
                <img src="https://cdn.shopify.com/s/files/1/0733/4071/5165/files/nutritional-icon.png?v=1773783298&width=80" alt="" width="40" height="50" loading="lazy">
              </span>
              <span>View Nutritional Information Chart</span>
            </a>
            <dialog id="${nutritionId}" class="bundle-nutrition-modal">
              <button type="button" class="bundle-nutrition-modal__scrim" aria-label="Close" data-bundle-nutrition-close></button>
              <div class="bundle-nutrition-modal__panel">
                <button type="button" class="bundle-nutrition-modal__dismiss" aria-label="Close" data-bundle-nutrition-close>×</button>
                <img class="bundle-nutrition-modal__image" src="${this.escape(
                  nutritionUrl
                )}" alt="${this.escape(title)}" loading="lazy">
              </div>
            </dialog>`
          : '';

        return `
          <li class="bundle-included-products__item">
            <div class="bundle-included-card">
              <div class="bundle-included-card__media">
                ${cardImageHtml}
                <span class="bundle-included-card__badge" aria-label="${qty} included">${qty}</span>
              </div>
              <div class="bundle-included-card__info">
                <p class="bundle-included-card__title">${this.escape(title)}</p>
                ${
                  weight
                    ? `<p class="bundle-included-card__meta">${this.escape(weight)}</p>`
                    : ''
                }
                <button type="button" class="bundle-included-card__quick-view btn--link" aria-controls="${modalId}" aria-haspopup="dialog">Quick View</button>
              </div>
            </div>
            <bundle-quick-view-modal id="${modalId}" class="bundle-quick-view" hidden>
              <div class="fixed-overlay" data-bundle-quick-view-overlay></div>
              <div class="bundle-quick-view__dialog" role="dialog" aria-modal="true" aria-labelledby="${modalId}-title">
                <button type="button" class="drawer__close-btn z-2" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--large icon--thick" fill="none" viewBox="0 0 24 24">
                    <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div class="bundle-quick-view__layout">
                  <div class="bundle-quick-view__gallery">
                    <div class="bundle-quick-view__main-media">
                      ${gallerySlides || '<div class="bundle-quick-view__slide is-active" data-index="0"><div class="bundle-quick-view__image placeholder-svg"></div></div>'}
                    </div>
                    ${galleryThumbs}
                  </div>
                  <div class="bundle-quick-view__content">
                    <h2 id="${modalId}-title" class="bundle-quick-view__title">${this.escape(
                      title
                    )}</h2>
                    ${
                      description
                        ? `<div class="bundle-quick-view__description">${this.escape(
                            description
                          )}</div>`
                        : ''
                    }
                    <div class="bundle-quick-view__specs">
                      <p class="bundle-quick-view__specs-heading">Product Specs</p>
                      <div class="bundle-specs-table">
                        <table class="bundle-specs-table__table"><tbody>${specsRows}</tbody></table>
                        ${nutritionHtml}
                      </div>
                    </div>
                    <a href="${this.escape(url)}" class="bundle-quick-view__cta btn btn--secondary">See Product Details</a>
                  </div>
                </div>
              </div>
            </bundle-quick-view-modal>
          </li>
        `;
      }

      bindQuickViews() {
        this.querySelectorAll('bundle-quick-view-modal').forEach((modal) => {
          if (customElements.get('bundle-quick-view-modal') && !modal._bundleBound) {
            customElements.upgrade(modal);
          }
        });
      }

      escape(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}

if (!customElements.get('bundle-quick-view-modal')) {
  customElements.define(
    'bundle-quick-view-modal',
    class BundleQuickViewModal extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.parentElementBeforeAppend = this.parentElement;

        this.bindControls(signal);

        this.querySelector('.fixed-overlay')?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
          },
          { signal }
        );

        this.querySelector('.drawer__close-btn')?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
          },
          { signal }
        );

        document.addEventListener(
          'keyup',
          (event) => {
            if (event.code === 'Escape' && this.open) this.hide();
          },
          { signal }
        );

        this.querySelectorAll('.bundle-quick-view__thumb').forEach((thumb) => {
          thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.index)), {
            signal,
          });
        });
      }

      bindControls(signal) {
        document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((control) => {
          // Only external triggers open the panel (not overlay / close inside it)
          if (control.closest('bundle-quick-view-modal') === this) return;
          control.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              this.show();
            },
            { signal }
          );
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get open() {
        return this.hasAttribute('open');
      }

      show() {
        // Lift above product stacking contexts (same approach as cart drawer)
        if (this.parentElement !== document.body) {
          this.parentElementBeforeAppend = this.parentElement;
          document.body.append(this);
        }

        this.hidden = false;
        document.documentElement.classList.add('overflow-hidden');

        requestAnimationFrame(() => {
          this.setAttribute('open', '');
          this.setAttribute('active', '');
          this.querySelector('.drawer__close-btn')?.focus();
        });
      }

      hide() {
        this.removeAttribute('open');
        this.removeAttribute('active');

        window.setTimeout(() => {
          this.hidden = true;
          document.documentElement.classList.remove('overflow-hidden');

          if (
            this.parentElement === document.body &&
            this.parentElementBeforeAppend &&
            document.contains(this.parentElementBeforeAppend)
          ) {
            this.parentElementBeforeAppend.appendChild(this);
          }
        }, 400);
      }

      showSlide(index) {
        this.querySelectorAll('.bundle-quick-view__slide').forEach((slide) => {
          slide.classList.toggle('is-active', Number(slide.dataset.index) === index);
        });
        this.querySelectorAll('.bundle-quick-view__thumb').forEach((thumb) => {
          thumb.classList.toggle('is-active', Number(thumb.dataset.index) === index);
        });
      }
    }
  );
}

// Shared nutrition chart modal open/close (Product Specs + Quick View)
if (document.documentElement.dataset.bundleNutritionBound !== 'true') {
  document.documentElement.dataset.bundleNutritionBound = 'true';
  document.addEventListener('click', (event) => {
    const openLink = event.target.closest('[data-bundle-nutrition-open]');
    if (openLink) {
      event.preventDefault();
      const id = openLink.getAttribute('data-bundle-nutrition-open');
      const dialog = document.getElementById(id);
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
      }
      return;
    }

    const closeBtn = event.target.closest('[data-bundle-nutrition-close]');
    if (closeBtn) {
      const dialog = closeBtn.closest('dialog');
      if (dialog) dialog.close();
    }
  });
}
